import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, where, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebaseConfig';
import { Task } from '../../types/labeling';

interface TaskWithCount {
  task: Task;
  count: number;
}

export default function SchemaNeeded() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Record<string, Task>>({});
  const [approvedSubmissions, setApprovedSubmissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<"submissions" | "tasks">("submissions");
  const [filters, setFilters] = useState({
    category: 'all',
    subcategory: 'all',
    itemType: 'all',
    task: 'all',
  });
  const [sortBy, setSortBy] = useState<'count_desc' | 'count_asc' | 'title'>('count_desc');

  // Load tasks for schema checking
  useEffect(() => {
    const tasksQuery = collection(db, 'tasks');
    const unsubscribe = onSnapshot(tasksQuery, (snapshot) => {
      const tasksMap: Record<string, Task> = {};
      snapshot.docs.forEach(doc => {
        const task = { id: doc.id, ...doc.data() } as Task;
        tasksMap[task.id] = task;
      });
      setTasks(tasksMap);
    });
    return unsubscribe;
  }, []);

  // Load approved submissions
  useEffect(() => {
    const loadApprovedSubmissions = async () => {
      try {
        const submissionsRef = collection(db, 'submissions');
        const approvedQuery = query(submissionsRef, where('status', '==', 'approved'));
        const approvedSnapshot = await getDocs(approvedQuery);
        
        const submissions = approvedSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setApprovedSubmissions(submissions);
        setLoading(false);
      } catch (error) {
        console.error('Error loading approved submissions:', error);
        setLoading(false);
      }
    };

    loadApprovedSubmissions();
  }, []);

  const taskNeedsSchema = (task: Task) => {
    // Check for new detection schema structure
    if (task.detection?.enabled && task.detection?.schemaRef?.id) {
      return false; // Has detection schema
    }
    
    // Check for legacy schema structure
    if (task.labelSchemaRef?.id) {
      return false; // Has legacy schema
    }
    
    return true; // No schema found
  };

  // Get tasks that need schemas with submission counts
  const tasksNeedingSchema = useMemo(() => {
    const taskCounts: Record<string, number> = {};
    
    // Count submissions per task that need schemas
    approvedSubmissions.forEach(sub => {
      const task = tasks[sub.taskId];
      if (task && taskNeedsSchema(task)) {
        taskCounts[task.id] = (taskCounts[task.id] || 0) + 1;
      }
    });

    // Build array of tasks with counts
    const tasksWithCounts: TaskWithCount[] = [];
    Object.keys(tasks).forEach(taskId => {
      const task = tasks[taskId];
      if (taskNeedsSchema(task)) {
        tasksWithCounts.push({
          task,
          count: taskCounts[taskId] || 0
        });
      }
    });

    // Sort by task title
    return tasksWithCounts.sort((a, b) => a.task.title.localeCompare(b.task.title));
  }, [approvedSubmissions, tasks]);

  // ---- Field accessors ----
  const tCategory = (t: any) => t?.category || '';
  const tSubcategory = (t: any) => t?.subcategory || '';
  const tItemType = (t: any) => t?.itemType || '';

  // ---- Base pool: respects the view-mode toggle only ----
  const basePool = useMemo(() => {
    if (viewMode === "submissions") {
      return tasksNeedingSchema.filter(item => item.count > 0);
    }
    return tasksNeedingSchema;
  }, [tasksNeedingSchema, viewMode]);

  // ---- Filter option lists (cascading) ----
  const uniqueCategories = useMemo(
    () => [...new Set(basePool.map(i => tCategory(i.task)).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [basePool]
  );

  const uniqueSubcategories = useMemo(() => {
    const pool = filters.category === 'all'
      ? basePool
      : basePool.filter(i => tCategory(i.task) === filters.category);
    return [...new Set(pool.map(i => tSubcategory(i.task)).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [basePool, filters.category]);

  const uniqueItemTypes = useMemo(() => {
    const pool = basePool
      .filter(i => filters.category === 'all' || tCategory(i.task) === filters.category)
      .filter(i => filters.subcategory === 'all' || tSubcategory(i.task) === filters.subcategory);
    return [...new Set(pool.map(i => tItemType(i.task)).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [basePool, filters.category, filters.subcategory]);

  const uniqueTasks = useMemo(() => {
    const pool = basePool
      .filter(i => filters.category === 'all' || tCategory(i.task) === filters.category)
      .filter(i => filters.subcategory === 'all' || tSubcategory(i.task) === filters.subcategory)
      .filter(i => filters.itemType === 'all' || tItemType(i.task) === filters.itemType);
    return pool
      .map(i => [i.task.id, i.task.title || i.task.id] as [string, string])
      .sort((a, b) => a[1].localeCompare(b[1]));
  }, [basePool, filters.category, filters.subcategory, filters.itemType]);

  // ---- Filtered + sorted rows ----
  const filteredTasks = useMemo(() => {
    const rows = basePool.filter(i => {
      if (filters.category !== 'all' && tCategory(i.task) !== filters.category) return false;
      if (filters.subcategory !== 'all' && tSubcategory(i.task) !== filters.subcategory) return false;
      if (filters.itemType !== 'all' && tItemType(i.task) !== filters.itemType) return false;
      if (filters.task !== 'all' && i.task.id !== filters.task) return false;
      return true;
    });

    const sorted = [...rows];
    if (sortBy === 'count_desc') {
      sorted.sort((a, b) => b.count - a.count || (a.task.title || '').localeCompare(b.task.title || ''));
    } else if (sortBy === 'count_asc') {
      sorted.sort((a, b) => a.count - b.count || (a.task.title || '').localeCompare(b.task.title || ''));
    } else {
      sorted.sort((a, b) => (a.task.title || '').localeCompare(b.task.title || ''));
    }
    return sorted;
  }, [basePool, filters, sortBy]);

  // Calculate stats
  const stats = useMemo(() => {
    // For submissions mode: only count tasks with approved submissions
    const tasksWithSubmissions = tasksNeedingSchema.filter(item => item.count > 0);
    const totalSubmissions = tasksWithSubmissions.reduce((sum, item) => sum + item.count, 0);
    
    // For tasks mode: count all tasks needing schema
    const totalTasks = tasksNeedingSchema.length;

    return {
      tasksWithSubmissions: tasksWithSubmissions.length,
      totalSubmissions,
      totalTasks
    };
  }, [tasksNeedingSchema]);

  const handleCreateSchema = () => {
    navigate('/labeling/schemas?createSchema=true');
  };

  const handleAddSchema = (taskId: string) => {
    navigate(`/create-task?id=${taskId}`);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Schema Needed</h1>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900">Schema Needed</h1>
      
      {/* Filter Boxes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div 
          className={`p-6 rounded-lg border-2 shadow-sm text-center cursor-pointer transition-colors ${
            viewMode === "submissions"
              ? "bg-white border-blue-500"
              : "bg-white border-gray-200 hover:bg-gray-50"
          }`}
          onClick={() => setViewMode("submissions")}
        >
          <div className="text-2xl font-bold text-gray-900">
            {stats.tasksWithSubmissions}
          </div>
          <div className="text-sm text-gray-600 mt-1">Tasks Needing Schema</div>
        </div>
        <div 
          className={`p-6 rounded-lg border-2 shadow-sm text-center cursor-pointer transition-colors ${
            viewMode === "submissions"
              ? "bg-white border-blue-500"
              : "bg-white border-gray-200 hover:bg-gray-50"
          }`}
          onClick={() => setViewMode("submissions")}
        >
          <div className="text-2xl font-bold text-gray-900">
            {stats.totalSubmissions}
          </div>
          <div className="text-sm text-gray-600 mt-1">Submissions Needing Schema</div>
        </div>
        <div 
          className={`p-6 rounded-lg border-2 shadow-sm text-center cursor-pointer transition-colors ${
            viewMode === "tasks"
              ? "bg-white border-blue-500"
              : "bg-white border-gray-200 hover:bg-gray-50"
          }`}
          onClick={() => setViewMode("tasks")}
        >
          <div className="text-2xl font-bold text-gray-900">
            {stats.totalTasks}
          </div>
          <div className="text-sm text-gray-600 mt-1">All Tasks Needing Schema</div>
        </div>
      </div>

      {/* Tasks Table */}
      {filteredTasks.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg mb-2">
            {viewMode === "submissions" 
              ? "No tasks with approved submissions need schemas"
              : "No tasks need schemas"}
          </div>
          <p className="text-gray-400 mb-4">
            {viewMode === "submissions"
              ? "All tasks with approved submissions have schemas assigned."
              : "All tasks have schemas assigned."}
          </p>
        </div>
      ) : (
        <>
        {/* Filters + Sort */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
            <select
              value={filters.category}
              onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value, subcategory: 'all', itemType: 'all', task: 'all' }))}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              {uniqueCategories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Subcategory</label>
            <select
              value={filters.subcategory}
              onChange={(e) => setFilters(prev => ({ ...prev, subcategory: e.target.value, itemType: 'all', task: 'all' }))}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Subcategories</option>
              {uniqueSubcategories.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Item Type</label>
            <select
              value={filters.itemType}
              onChange={(e) => setFilters(prev => ({ ...prev, itemType: e.target.value, task: 'all' }))}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Item Types</option>
              {uniqueItemTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Task</label>
            <select
              value={filters.task}
              onChange={(e) => setFilters(prev => ({ ...prev, task: e.target.value }))}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Tasks</option>
              {uniqueTasks.map(([id, title]) => <option key={id} value={id}>{title}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'count_desc' | 'count_asc' | 'title')}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="count_desc">Most Submissions</option>
              <option value="count_asc">Fewest Submissions</option>
              <option value="title">Task Name (A-Z)</option>
            </select>
          </div>
          <div>
            <button
              type="button"
              onClick={() => { setFilters({ category: 'all', subcategory: 'all', itemType: 'all', task: 'all' }); setSortBy('count_desc'); }}
              className="w-full px-3 py-1.5 text-sm rounded bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>

        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {/* Table Header */}
          <div className={`hidden md:grid gap-4 px-6 py-3 bg-gray-100 text-xs font-semibold text-gray-600 uppercase tracking-wide border-b border-gray-200 ${
            viewMode === "submissions" ? "grid-cols-5" : "grid-cols-4"
          }`}>
            <div>Task Name</div>
            <div>Category</div>
            <div>Subcategory</div>
            {viewMode === "submissions" && (
              <div className="text-center">Submissions Needing Schema</div>
            )}
            <div className="text-right">Actions</div>
          </div>

          <ul className="divide-y divide-gray-200">
            {filteredTasks.map(({ task, count }) => (
              <li key={task.id} className="px-6 py-4 hover:bg-gray-50 bg-yellow-50">
                <div className={`grid md:items-center gap-4 ${
                  viewMode === "submissions" ? "md:grid-cols-5" : "md:grid-cols-4"
                }`}>
                          <div className="text-sm font-medium text-gray-900">
                    {task.title || task.id}
                    </div>

                  <div className="text-sm text-gray-900">
                    {(task as any).category || '—'}
                  </div>

                  <div className="text-sm text-gray-900">
                    {(task as any).subcategory || '—'}
                  </div>

                  {viewMode === "submissions" && (
                    <div className="text-sm text-gray-900 text-center">
                      <span className="font-semibold">{count}</span>
                        </div>
                      )}

                  <div className="flex md:justify-end gap-2">
                      <button
                      onClick={handleCreateSchema}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                      >
                      Create Schema
                      </button>
                    <button
                      onClick={() => handleAddSchema(task.id)}
                      className="px-3 py-1 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors"
                    >
                      Add Schema
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
        </>
      )}
    </div>
  );
}
